import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { 
  Users, Plus, Upload, Trash2, ChevronRight, 
  Download, Edit2, X, Check, UserPlus, FileText, ClipboardList, Activity,
  KeyRound, Printer, Copy, FolderOpen
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import PrescriptiveReport from '@/components/PrescriptiveReport';
import LiveClassMonitor from '@/components/LiveClassMonitor';
import MosaicIntakeModal from '@/components/mosaic/MosaicIntakeModal';
import MosaicRosterImportModal from '@/components/mosaic/MosaicRosterImportModal';
import MosaicGroupingModal from '@/components/mosaic/MosaicGroupingModal';
import MosaicStudentFlags from '@/components/mosaic/MosaicStudentFlags';
import { Sparkles, Users as UsersIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClassManagement = ({ initialView = 'classes' }) => {
  const { user } = useAuth();
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [classStudents, setClassStudents] = useState([]);
  // v6.32.162 — MOSAIC intake modal
  const [mosaicStudent, setMosaicStudent] = useState(null);
  // v6.32.163 — MOSAIC roster CSV import
  const [showRosterImport, setShowRosterImport] = useState(false);
  // v6.32.164 — MOSAIC grouping engine + intake-row flags
  const [showGrouping, setShowGrouping] = useState(false);
  const [intakeByStudent, setIntakeByStudent] = useState({});
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReportCard, setShowReportCard] = useState(false);
  const [showLiveMonitor, setShowLiveMonitor] = useState(false);
  const [showAllStudents, setShowAllStudents] = useState(initialView === 'students');
  const [allStudents, setAllStudents] = useState([]);
  const fileInputRef = useRef(null);

  const [newClass, setNewClass] = useState({ name: '', grade_level: 'K' });
  const [newStudent, setNewStudent] = useState({ name: '', email: '' });
  const [credentialCard, setCredentialCard] = useState(null);
  const [resetResult, setResetResult] = useState(null);

  const gradeOptions = ['K', '1', '2', '3-4', '5-6', '7-8', '9-12'];

  const fetchClasses = async () => {
    try {
      const res = await axios.get(`${API}/classes`);
      setClasses(res.data.classes || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchClassDetail = async (classId) => {
    try {
      const res = await axios.get(`${API}/classes/${classId}`);
      const students = res.data.students || [];
      setClassStudents(students);
      setSelectedClass(res.data);
      // v6.32.164 — lazy-load MOSAIC intake flags for badge display.
      try {
        const ids = students.map((s) => s.user_id).filter(Boolean);
        const map = {};
        await Promise.all(ids.map(async (sid) => {
          try {
            const r = await axios.get(`${API}/mosaic/intake/${sid}`);
            if (r.data?.intake) map[sid] = r.data.intake;
          } catch (e) { /* ignore */ }
        }));
        setIntakeByStudent(map);
      } catch (e) { /* ignore */ }
    } catch (e) {
      toast.error('Failed to load class details');
    }
  };

  useEffect(() => { fetchClasses(); }, []);

  useEffect(() => { setShowAllStudents(initialView === 'students'); }, [initialView]);

  // Fetch all students across all classes when in "all students" view
  useEffect(() => {
    if (showAllStudents && classes.length > 0) {
      const fetchAll = async () => {
        const students = [];
        for (const cls of classes) {
          try {
            const res = await axios.get(`${API}/classes/${cls.class_id}`);
            const s = res.data.students || [];
            s.forEach(st => students.push({ ...st, class_name: cls.name, grade_level: cls.grade_level }));
          } catch {}
        }
        setAllStudents(students);
      };
      fetchAll();
    }
  }, [showAllStudents, classes]);

  const handleCreateClass = async () => {
    if (!newClass.name.trim()) { toast.error('Class name is required'); return; }
    try {
      await axios.post(`${API}/classes`, newClass);
      toast.success('Class created');
      setShowCreateForm(false);
      setNewClass({ name: '', grade_level: 'K' });
      fetchClasses();
    } catch (e) {
      toast.error('Failed to create class');
    }
  };

  const handleUpdateClass = async (classId) => {
    try {
      await axios.put(`${API}/classes/${classId}`, editingClass);
      toast.success('Class updated');
      setEditingClass(null);
      fetchClasses();
      if (selectedClass?.class_id === classId) fetchClassDetail(classId);
    } catch (e) {
      toast.error('Failed to update class');
    }
  };

  const handleDeleteClass = async (classId) => {
    if (!window.confirm('Delete this class? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/classes/${classId}`);
      toast.success('Class deleted');
      if (selectedClass?.class_id === classId) { setSelectedClass(null); setClassStudents([]); }
      fetchClasses();
    } catch (e) {
      toast.error('Failed to delete class');
    }
  };

  const handleAddStudent = async () => {
    if (!newStudent.name.trim()) { toast.error('Student name is required'); return; }
    try {
      const res = await axios.post(`${API}/classes/${selectedClass.class_id}/students`, newStudent);
      toast.success('Student added');
      setShowAddStudent(false);
      setNewStudent({ name: '', email: '' });
      if (res.data.password) {
        setCredentialCard({ name: res.data.name, email: res.data.email, password: res.data.password });
      }
      fetchClassDetail(selectedClass.class_id);
    } catch (e) {
      toast.error('Failed to add student');
    }
  };

  const handleResetPassword = async (studentId, studentName) => {
    if (!window.confirm(`Reset password for ${studentName}? A new password will be generated.`)) return;
    try {
      const res = await axios.post(`${API}/classes/${selectedClass.class_id}/students/${studentId}/reset-password`);
      setResetResult({ name: res.data.name, email: res.data.email, password: res.data.password });
      toast.success(`Password reset for ${studentName}`);
    } catch (e) {
      toast.error('Failed to reset password');
    }
  };

  // v6.32.114 — WIDA accommodations toggle (RA, ES, WD, HL).
  const handleToggleAccommodation = async (student, code) => {
    const current = student.accommodations || [];
    const next = current.includes(code) ? current.filter((c) => c !== code) : [...current, code];
    try {
      await axios.patch(
        `${API}/classes/${selectedClass.class_id}/students/${student.user_id}/accommodations`,
        { accommodations: next }
      );
      // Optimistic local update so the toggle feels instant
      setClassStudents((rows) =>
        rows.map((r) => (r.user_id === student.user_id ? { ...r, accommodations: next } : r))
      );
      toast.success(`${code} ${next.includes(code) ? 'enabled' : 'disabled'} for ${student.name}`);
    } catch (e) {
      toast.error('Failed to update accommodations');
    }
  };

  const printCredentialCard = (cred) => {
    const win = window.open('', '_blank', 'width=400,height=300');
    win.document.write(`
      <html><head><title>Student Login Card</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; }
        .card { border: 2px solid #002FA7; border-radius: 8px; padding: 24px; max-width: 350px; margin: 0 auto; }
        .logo { color: #002FA7; font-size: 18px; font-weight: bold; margin-bottom: 4px; }
        .sub { color: #6B7280; font-size: 11px; margin-bottom: 16px; }
        .field { margin-bottom: 10px; }
        .label { color: #6B7280; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; }
        .value { font-size: 16px; font-weight: bold; color: #111827; font-family: monospace; background: #F3F4F6; padding: 6px 10px; border-radius: 4px; margin-top: 2px; }
        .footer { color: #9CA3AF; font-size: 9px; margin-top: 16px; text-align: center; }
        @media print { body { margin: 0; } }
      </style></head><body>
      <div class="card">
        <div class="logo">MOSAIC</div>
        <div class="sub">Student Login Card</div>
        <div class="field"><div class="label">Name</div><div class="value">${cred.name}</div></div>
        <div class="field"><div class="label">Email / Username</div><div class="value">${cred.email}</div></div>
        <div class="field"><div class="label">Password</div><div class="value">${cred.password}</div></div>
        <div class="footer">Keep this card safe. Your teacher can reset your password if you forget it.</div>
      </div>
      <script>window.print();</script>
      </body></html>
    `);
    win.document.close();
  };

  const copyCredentials = (cred) => {
    const text = `Name: ${cred.name}\nEmail: ${cred.email}\nPassword: ${cred.password}`;
    navigator.clipboard.writeText(text).then(() => toast.success('Credentials copied to clipboard'));
  };

  const handleRemoveStudent = async (studentId) => {
    if (!window.confirm('Remove student from class?')) return;
    try {
      await axios.delete(`${API}/classes/${selectedClass.class_id}/students/${studentId}`);
      toast.success('Student removed');
      fetchClassDetail(selectedClass.class_id);
    } catch (e) {
      toast.error('Failed to remove student');
    }
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(
        `${API}/classes/${selectedClass.class_id}/import-csv`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setImportResult(res.data);
      toast.success(res.data.message);
      fetchClassDetail(selectedClass.class_id);
    } catch (e) {
      toast.error('CSV import failed');
    }
    e.target.value = '';
  };

  const handleExportClassPdf = async () => {
    try {
      const res = await axios.get(`${API}/classes/${selectedClass.class_id}/report/pdf`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Class_Report_${selectedClass.name.replace(/\s+/g, '_')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (e) {
      toast.error('Failed to export PDF');
    }
  };

  const handleInviteParent = async (studentId, studentName) => {
    const email = window.prompt(`Enter parent email for ${studentName}:`);
    if (!email || !email.includes('@')) return;
    try {
      await axios.post(`${API}/parent/invite`, { parent_email: email, student_id: studentId });
      toast.success(`Parent invite sent to ${email}`);
    } catch (e) {
      toast.error('Failed to invite parent');
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-400">Loading classes...</div>;

  // Show Prescriptive Report Card view
  if (showReportCard && selectedClass) {
    return (
      <PrescriptiveReport
        classId={selectedClass.class_id}
        className={selectedClass.name}
        onBack={() => setShowReportCard(false)}
      />
    );
  }

  // Show Live Monitor view
  if (showLiveMonitor && selectedClass) {
    return (
      <LiveClassMonitor
        classId={selectedClass.class_id}
        className={selectedClass.name}
        onBack={() => setShowLiveMonitor(false)}
      />
    );
  }

  return (
    <div data-testid="class-management">
      {/* View Toggle */}
      <div className="flex gap-2 mb-4">
        <Button
          size="sm"
          variant={!showAllStudents ? "default" : "outline"}
          onClick={() => setShowAllStudents(false)}
          className={`text-xs h-8 ${!showAllStudents ? 'bg-[#002FA7] text-white' : ''}`}
          style={{ borderRadius: 0 }}
          data-testid="view-classes-btn"
        >
          <FolderOpen size={13} className="mr-1" /> My Classes ({classes.length})
        </Button>
        <Button
          size="sm"
          variant={showAllStudents ? "default" : "outline"}
          onClick={() => setShowAllStudents(true)}
          className={`text-xs h-8 ${showAllStudents ? 'bg-[#002FA7] text-white' : ''}`}
          style={{ borderRadius: 0 }}
          data-testid="view-students-btn"
        >
          <Users size={13} className="mr-1" /> All Students ({classes.reduce((sum, c) => sum + (c.student_count || 0), 0)})
        </Button>
      </div>

      {/* All Students View */}
      {showAllStudents ? (
        <div className="border-2 border-gray-200 bg-white" data-testid="all-students-view">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-bold text-sm uppercase tracking-wider text-gray-600">All Students Across Classes</h3>
            <p className="text-xs text-gray-400 mt-1">{allStudents.length} students in {classes.length} classes</p>
          </div>
          {allStudents.length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              <Users className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No students enrolled yet</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {allStudents.map((s, i) => (
                <div key={s.student_id || i} className="p-3 flex items-center justify-between hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-50 border border-[#002FA7] flex items-center justify-center text-xs font-bold text-[#002FA7]">
                      {s.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div>
                      <div className="text-sm font-semibold">{s.name}</div>
                      <div className="text-xs text-gray-400">{s.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className="px-2 py-0.5 bg-gray-100 border border-gray-200 font-semibold">{s.class_name}</span>
                    <span className="text-gray-400">Grade {s.grade_level}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (

      <div className="grid md:grid-cols-3 gap-6">
      {/* Class List */}
      <div className="md:col-span-1">
        <div className="border-2 border-gray-200">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-bold text-sm uppercase tracking-wider text-gray-600">My Classes</h3>
            <Button size="sm" variant="outline" className="btn-sharp h-8" onClick={() => setShowCreateForm(true)} data-testid="create-class-btn">
              <Plus size={14} className="mr-1" /> New
            </Button>
          </div>

          {showCreateForm && (
            <div className="p-4 border-b border-gray-200 bg-blue-50 space-y-3" data-testid="create-class-form">
              <input
                type="text" placeholder="Class name" value={newClass.name}
                onChange={(e) => setNewClass({ ...newClass, name: e.target.value })}
                className="w-full border border-gray-300 px-3 py-2 text-sm"
                data-testid="class-name-input"
              />
              <select
                value={newClass.grade_level}
                onChange={(e) => setNewClass({ ...newClass, grade_level: e.target.value })}
                className="w-full border border-gray-300 px-3 py-2 text-sm"
                data-testid="class-grade-select"
              >
                {gradeOptions.map(g => <option key={g} value={g}>Grade {g}</option>)}
              </select>
              <div className="flex gap-2">
                <Button size="sm" className="btn-sharp-primary h-8 flex-1" onClick={handleCreateClass} data-testid="save-class-btn">
                  <Check size={14} className="mr-1" /> Create
                </Button>
                <Button size="sm" variant="outline" className="btn-sharp h-8" onClick={() => setShowCreateForm(false)}>
                  <X size={14} />
                </Button>
              </div>
            </div>
          )}

          {classes.length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              <Users className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No classes yet</p>
            </div>
          ) : (
            classes.map(c => (
              <div
                key={c.class_id}
                className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors flex items-center justify-between ${selectedClass?.class_id === c.class_id ? 'bg-blue-50 border-l-4 border-l-[#002FA7]' : ''}`}
                onClick={() => fetchClassDetail(c.class_id)}
                data-testid={`class-item-${c.class_id}`}
              >
                <div>
                  <div className="font-semibold text-sm">{c.name}</div>
                  <div className="text-xs text-gray-500">Grade {c.grade_level} &bull; {c.student_ids?.length || 0} students</div>
                </div>
                <ChevronRight size={16} className="text-gray-400" />
              </div>
            ))
          )}
        </div>
      </div>

      {/* Class Detail */}
      <div className="md:col-span-2">
        {!selectedClass ? (
          <div className="border-2 border-dashed border-gray-200 p-12 text-center text-gray-400">
            <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p>Select a class to manage students</p>
          </div>
        ) : (
          <div className="border-2 border-gray-200">
            {/* Class Header */}
            <div className="p-4 border-b border-gray-200 flex items-center justify-between" data-testid="class-detail-header">
              <div>
                {editingClass ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text" value={editingClass.name}
                      onChange={(e) => setEditingClass({ ...editingClass, name: e.target.value })}
                      className="border border-gray-300 px-2 py-1 text-sm font-semibold"
                    />
                    <select
                      value={editingClass.grade_level}
                      onChange={(e) => setEditingClass({ ...editingClass, grade_level: e.target.value })}
                      className="border border-gray-300 px-2 py-1 text-sm"
                    >
                      {gradeOptions.map(g => <option key={g} value={g}>Grade {g}</option>)}
                    </select>
                    <Button size="sm" variant="outline" className="h-7" onClick={() => handleUpdateClass(selectedClass.class_id)}>
                      <Check size={14} />
                    </Button>
                    <Button size="sm" variant="outline" className="h-7" onClick={() => setEditingClass(null)}>
                      <X size={14} />
                    </Button>
                  </div>
                ) : (
                  <>
                    <h3 className="font-bold text-lg">{selectedClass.name}</h3>
                    <span className="text-sm text-gray-500">Grade {selectedClass.grade_level} &bull; {classStudents.length} students</span>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                {!editingClass && (
                  <Button size="sm" variant="outline" className="btn-sharp h-8" onClick={() => setEditingClass({ name: selectedClass.name, grade_level: selectedClass.grade_level })} data-testid="edit-class-btn">
                    <Edit2 size={14} />
                  </Button>
                )}
                <Button size="sm" variant="outline" className="btn-sharp h-8" onClick={handleExportClassPdf} data-testid="export-class-pdf-btn">
                  <Download size={14} className="mr-1" /> PDF
                </Button>
                <Button size="sm" variant="outline" className="btn-sharp h-8" onClick={() => window.open(`${API}/export/class/${selectedClass.class_id}`, '_blank')} data-testid="export-class-csv-btn">
                  <Download size={14} className="mr-1" /> CSV
                </Button>
                <Button size="sm" className="btn-sharp-primary h-8" onClick={() => setShowReportCard(true)} data-testid="prescriptive-report-btn">
                  <ClipboardList size={14} className="mr-1" /> Report Card
                </Button>
                <Button size="sm" variant="outline" className="btn-sharp h-8 border-green-300 text-green-700 hover:bg-green-50" onClick={() => setShowLiveMonitor(true)} data-testid="live-monitor-btn">
                  <Activity size={14} className="mr-1" /> Live Monitor
                </Button>
                <Button size="sm" variant="outline" className="btn-sharp h-8 text-red-600 hover:bg-red-50" onClick={() => handleDeleteClass(selectedClass.class_id)} data-testid="delete-class-btn">
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>

            {/* Actions Bar */}
            <div className="p-3 border-b border-gray-200 bg-gray-50 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" className="btn-sharp h-8" onClick={() => setShowAddStudent(true)} data-testid="add-student-btn">
                <UserPlus size={14} className="mr-1" /> Add Student
              </Button>
              <Button size="sm" variant="outline" className="btn-sharp h-8" onClick={() => fileInputRef.current?.click()} data-testid="csv-import-btn">
                <Upload size={14} className="mr-1" /> Import CSV
              </Button>
              <Button size="sm" variant="outline" className="btn-sharp h-8 border-violet-300 text-violet-700 hover:bg-violet-50" onClick={() => setShowRosterImport(true)} data-testid="mosaic-roster-import-btn">
                <Sparkles size={14} className="mr-1" /> MOSAIC Roster Import
              </Button>
              <Button size="sm" variant="outline" className="btn-sharp h-8 border-violet-300 text-violet-700 hover:bg-violet-50" onClick={() => setShowGrouping(true)} data-testid="mosaic-grouping-btn">
                <UsersIcon size={14} className="mr-1" /> Suggest Groups
              </Button>
              <input type="file" ref={fileInputRef} accept=".csv" onChange={handleCsvUpload} className="hidden" />
              <span className="text-xs text-gray-400 self-center ml-2">CSV format: name, email (optional)</span>
            </div>

            {/* Add Student Form */}
            {showAddStudent && (
              <div className="p-4 border-b border-gray-200 bg-blue-50 flex flex-wrap items-end gap-3" data-testid="add-student-form">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Name *</label>
                  <input
                    type="text" value={newStudent.name}
                    onChange={(e) => setNewStudent({ ...newStudent, name: e.target.value })}
                    className="border border-gray-300 px-3 py-2 text-sm w-48"
                    placeholder="Student name"
                    data-testid="student-name-input"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Email (optional)</label>
                  <input
                    type="email" value={newStudent.email}
                    onChange={(e) => setNewStudent({ ...newStudent, email: e.target.value })}
                    className="border border-gray-300 px-3 py-2 text-sm w-56"
                    placeholder="student@school.edu"
                    data-testid="student-email-input"
                  />
                </div>
                <Button size="sm" className="btn-sharp-primary h-9" onClick={handleAddStudent} data-testid="save-student-btn">
                  <Check size={14} className="mr-1" /> Add
                </Button>
                <Button size="sm" variant="outline" className="btn-sharp h-9" onClick={() => { setShowAddStudent(false); setNewStudent({ name: '', email: '' }); }}>
                  <X size={14} />
                </Button>
              </div>
            )}

            {/* Credential Card (shown after adding a student) */}
            {credentialCard && (
              <div className="p-4 border-b border-gray-200 bg-emerald-50" data-testid="credential-card">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-sm text-emerald-800 flex items-center gap-1"><KeyRound size={14} /> Student Login Credentials</span>
                  <Button size="sm" variant="ghost" className="h-6" onClick={() => setCredentialCard(null)}><X size={14} /></Button>
                </div>
                <div className="bg-white border-2 border-emerald-200 rounded-lg p-4 max-w-md">
                  <p className="text-[#002FA7] font-bold text-sm mb-1">MOSAIC — Student Login Card</p>
                  <div className="space-y-2 text-xs">
                    <div><span className="text-gray-400 uppercase text-[10px]">Name</span><div className="font-bold text-gray-800 font-mono bg-gray-50 px-2 py-1 rounded">{credentialCard.name}</div></div>
                    <div><span className="text-gray-400 uppercase text-[10px]">Email / Username</span><div className="font-bold text-gray-800 font-mono bg-gray-50 px-2 py-1 rounded">{credentialCard.email}</div></div>
                    <div><span className="text-gray-400 uppercase text-[10px]">Password</span><div className="font-bold text-gray-800 font-mono bg-gray-50 px-2 py-1 rounded">{credentialCard.password}</div></div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" className="bg-[#002FA7] h-7 text-xs" onClick={() => printCredentialCard(credentialCard)} data-testid="print-credential-btn">
                      <Printer size={12} className="mr-1" /> Print Card
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => copyCredentials(credentialCard)} data-testid="copy-credential-btn">
                      <Copy size={12} className="mr-1" /> Copy
                    </Button>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-2">Save or print this card — the password cannot be viewed again after closing.</p>
                </div>
              </div>
            )}

            {/* Reset Password Result */}
            {resetResult && (
              <div className="p-4 border-b border-gray-200 bg-amber-50" data-testid="reset-password-result">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-sm text-amber-800 flex items-center gap-1"><KeyRound size={14} /> Password Reset — New Credentials</span>
                  <Button size="sm" variant="ghost" className="h-6" onClick={() => setResetResult(null)}><X size={14} /></Button>
                </div>
                <div className="bg-white border-2 border-amber-200 rounded-lg p-4 max-w-md">
                  <div className="space-y-2 text-xs">
                    <div><span className="text-gray-400 uppercase text-[10px]">Name</span><div className="font-bold text-gray-800 font-mono bg-gray-50 px-2 py-1 rounded">{resetResult.name}</div></div>
                    <div><span className="text-gray-400 uppercase text-[10px]">Email / Username</span><div className="font-bold text-gray-800 font-mono bg-gray-50 px-2 py-1 rounded">{resetResult.email}</div></div>
                    <div><span className="text-gray-400 uppercase text-[10px]">New Password</span><div className="font-bold text-amber-700 font-mono bg-amber-50 px-2 py-1 rounded border border-amber-200">{resetResult.password}</div></div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" className="bg-[#002FA7] h-7 text-xs" onClick={() => printCredentialCard(resetResult)} data-testid="print-reset-btn">
                      <Printer size={12} className="mr-1" /> Print Card
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => copyCredentials(resetResult)} data-testid="copy-reset-btn">
                      <Copy size={12} className="mr-1" /> Copy
                    </Button>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-2">Give this new password to the student. The old password no longer works.</p>
                </div>
              </div>
            )}

            {/* Import Result */}
            {importResult && (
              <div className="p-4 border-b border-gray-200 bg-green-50" data-testid="csv-import-result">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-sm text-green-800">{importResult.message}</span>
                  <Button size="sm" variant="ghost" className="h-6" onClick={() => setImportResult(null)}><X size={14} /></Button>
                </div>
                {importResult.created?.length > 0 && (
                  <div className="text-xs text-green-700 space-y-1">
                    <div className="flex items-center justify-between">
                      <div className="font-semibold">New accounts created (save these credentials):</div>
                      <Button size="sm" variant="outline" className="h-6 text-[10px] border-green-300 text-green-700" onClick={() => {
                        const text = importResult.created.map(s => `${s.name} — ${s.email} / ${s.password}`).join('\n');
                        navigator.clipboard.writeText(text).then(() => toast.success('All credentials copied'));
                      }} data-testid="copy-all-csv-credentials">
                        <Copy size={10} className="mr-1" /> Copy All
                      </Button>
                    </div>
                    {importResult.created.map((s, i) => (
                      <div key={i} className="bg-white px-2 py-1 font-mono flex items-center justify-between">
                        <span>{s.name} &mdash; {s.email} / {s.password}</span>
                        <Button size="sm" variant="ghost" className="h-5 text-[10px] text-green-600" onClick={() => printCredentialCard(s)} data-testid={`print-csv-cred-${i}`}>
                          <Printer size={10} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                {importResult.errors?.length > 0 && (
                  <div className="text-xs text-red-600 mt-2">
                    {importResult.errors.map((e, i) => <div key={i}>Row {e.row}: {e.reason}</div>)}
                  </div>
                )}
              </div>
            )}

            {/* Student List */}
            <div>
              {classStudents.length === 0 ? (
                <div className="p-8 text-center text-gray-400">
                  <p className="text-sm">No students yet. Add individually or import from CSV.</p>
                </div>
              ) : (
                <table className="w-full text-sm" data-testid="student-table">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Name</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Email</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Role</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase" title="WIDA accommodations: RA = Repeat Audio, ES = Extended Speaking time, WD = Word Decoding (no spellcheck), HL = Highlighter">
                        Accommodations
                      </th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {classStudents.map(s => (
                      <tr key={s.user_id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`student-row-${s.user_id}`}>
                        <td className="px-4 py-3 font-medium">
                          <span>{s.name}</span>
                          <MosaicStudentFlags intake={intakeByStudent[s.user_id]} compact />
                        </td>
                        <td className="px-4 py-3 text-gray-500">{s.email}</td>
                        <td className="px-4 py-3 text-gray-500 capitalize">{s.role}</td>
                        <td className="px-4 py-3" data-testid={`accommodations-cell-${s.user_id}`}>
                          <div className="flex flex-wrap gap-1">
                            {[
                              { code: 'RA', label: 'RA', tip: 'Repeat Audio — unlimited replays on Listening' },
                              { code: 'ES', label: 'ES', tip: 'Extended Speaking time' },
                              { code: 'WD', label: 'WD', tip: 'Word Decoding — turn off spellcheck on writing' },
                              { code: 'HL', label: 'HL', tip: 'Highlighter tool on Reading + Writing' },
                            ].map(({ code, label, tip }) => {
                              const on = (s.accommodations || []).includes(code);
                              return (
                                <button
                                  key={code}
                                  type="button"
                                  title={tip}
                                  onClick={() => handleToggleAccommodation(s, code)}
                                  className={`px-2 py-0.5 text-[11px] font-bold tracking-wide border-2 transition-colors ${
                                    on
                                      ? 'bg-amber-100 border-amber-400 text-amber-800 hover:bg-amber-200'
                                      : 'bg-white border-gray-300 text-gray-400 hover:bg-gray-50'
                                  }`}
                                  data-testid={`accommodation-toggle-${code}-${s.user_id}`}
                                  aria-pressed={on}
                                >
                                  {label}
                                </button>
                              );
                            })}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right flex items-center justify-end gap-1">
                          <Button size="sm" variant="ghost" className="h-7 text-violet-600 hover:text-violet-700 hover:bg-violet-50 text-xs" onClick={() => setMosaicStudent(s)} data-testid={`mosaic-intake-btn-${s.user_id}`}>
                            <Sparkles size={12} className="mr-1" /> MOSAIC
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 text-amber-600 hover:text-amber-700 hover:bg-amber-50 text-xs" onClick={() => handleResetPassword(s.user_id, s.name)} data-testid={`reset-pw-${s.user_id}`}>
                            <KeyRound size={12} className="mr-1" /> Reset PW
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 text-blue-500 hover:text-blue-700 hover:bg-blue-50 text-xs" onClick={() => handleInviteParent(s.user_id, s.name)} data-testid={`invite-parent-${s.user_id}`}>
                            Invite Parent
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleRemoveStudent(s.user_id)} data-testid={`remove-student-${s.user_id}`}>
                            <Trash2 size={14} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
      )}
      {/* v6.32.162 — MOSAIC intake modal (per student row) */}
      <MosaicIntakeModal
        open={!!mosaicStudent}
        onOpenChange={(o) => { if (!o) setMosaicStudent(null); }}
        student={mosaicStudent}
      />
      {/* v6.32.163 — MOSAIC roster CSV import */}
      <MosaicRosterImportModal
        open={showRosterImport}
        onOpenChange={setShowRosterImport}
        classId={selectedClass?.class_id}
        onComplete={() => {
          setShowRosterImport(false);
          if (selectedClass?.class_id) {
            fetchClassDetail(selectedClass.class_id);
          }
        }}
      />
      {/* v6.32.164 — MOSAIC grouping engine */}
      <MosaicGroupingModal
        open={showGrouping}
        onOpenChange={setShowGrouping}
        classId={selectedClass?.class_id}
      />
    </div>
  );
};

export default ClassManagement;
